<script generics="T extends ChainId" lang="ts">
	import Check from 'lucide-svelte/icons/check'
	import ChevronsUpDown from 'lucide-svelte/icons/chevrons-up-down'
	import { tick } from 'svelte'

	import { Button } from '$lib/components/ui/button'
	import * as Command from '$lib/components/ui/command'
	import * as Popover from '$lib/components/ui/popover'
	import { CHAINS_CONFIG } from '$lib/constants/chainsConfig'
	import type { ChainId } from '$lib/models'
	import { cn } from '$lib/utils/shadcn'

	type Option = { value: T; label: string; disabled: boolean }

	export let placeholder
	export let noResults
	export let disabled = false
	export let options: Option[]

	let valueProps: T | T[] | null
	export { valueProps as value }
	let className: string | null = null
	export { className as class }
	export let onChange: ((value: T | T[] | null) => void) | null = null

	let open = false

	$: selectedValue = Array.isArray(valueProps)
		? options.filter(f => valueProps?.includes(f.value)).map(f => f.value)
		: options.find(f => f.value === valueProps)?.value

	// We want to refocus the trigger button when the user selects
	// an item from the list so users can continue navigating the
	// rest of the form with the keyboard.
	const closeAndFocusTrigger = (triggerId: string) => {
		open = false

		tick().then(() => {
			document.getElementById(triggerId)?.focus()
		})
	}
</script>

<Popover.Root preventScroll={false} bind:open let:ids>
	<Popover.Trigger asChild let:builder>
		<Button
			class={cn('justify-start gap-2', className)}
			aria-expanded={open}
			aria-label={placeholder}
			builders={[builder]}
			{disabled}
			role="combobox"
			variant="outline"
		>
			{#if Array.isArray(selectedValue)}
				{placeholder}
			{:else}
				{@const chainConfig = selectedValue ? CHAINS_CONFIG[selectedValue] : null}

				{#if chainConfig}
					<img
						class="mr-2 size-[18px]"
						alt="{chainConfig.name} logo"
						crossorigin="anonymous"
						decoding="async"
						fetchpriority="low"
						height="18"
						loading="eager"
						src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{chainConfig.slug}.svg"
						width="18"
					/>

					{chainConfig.name}
				{:else}
					{placeholder}
				{/if}
			{/if}

			<ChevronsUpDown class="ml-auto h-4 w-4 shrink-0 opacity-50" />
		</Button>
	</Popover.Trigger>

	<Popover.Content class="max-h-80 overflow-auto p-0" sameWidth side="bottom">
		<Command.Root>
			<Command.Input {placeholder} />
			<Command.Empty>{noResults}</Command.Empty>
			<Command.Group>
				{#each options as { value, label, disabled } (value)}
					{@const chainConfig = CHAINS_CONFIG[value]}

					<Command.Item
						{disabled}
						onSelect={currentValue => {
							if (Array.isArray(valueProps)) {
								// @ts-expect-error types mismatch
								if (valueProps.includes(currentValue)) {
									valueProps = valueProps.filter(v => v !== currentValue)
								} else {
									// @ts-expect-error types mismatch
									valueProps = [...valueProps, currentValue]
								}
							} else {
								// @ts-expect-error types mismatch
								valueProps = currentValue
								closeAndFocusTrigger(ids.trigger)
							}

							onChange?.(valueProps)
						}}
						{value}
					>
						{#if Array.isArray(valueProps) ? valueProps.includes(value) : valueProps === value}
							<Check class="mr-2" size="18" />
						{:else}
							<img
								class="mr-2 size-[18px]"
								alt="{chainConfig.name} logo"
								crossorigin="anonymous"
								decoding="async"
								fetchpriority="low"
								height="18"
								loading="eager"
								src="https://res.cloudinary.com/dcruwhddg/image/upload/icons/{chainConfig.slug}.svg"
								width="18"
							/>
						{/if}

						{label}
					</Command.Item>
				{/each}
			</Command.Group>
		</Command.Root>
	</Popover.Content>
</Popover.Root>
